import validateOrderV2 from '@dehaat/kisan-app-bl/api/validateOrderV2'
import { Address } from '@dehaat/kisan-app-bl/models/AddressV2'
import { Error, PaymentOptions } from '@dehaat/kisan-app-bl/models/Order'
import { Vendor } from '@dehaat/kisan-app-bl/models/Vendor'
import { isErrorCodeFromCart } from '@dehaat/kisan-app-bl/utils/cart'
import { AxiosError, AxiosHeaders } from 'axios'
import useTranslation from 'next-translate/useTranslation'
import { useCallback, useContext, useEffect, useState } from 'react'

import { CART_ERRORS } from '@/constants/cart'
import {
  ASSISTED_USER_NUMBER,
  AVAILABLE_COMBO_STOCK,
  AVAILABLE_STOCK,
  LOCALE_COOKIE_NAME,
  MY_CART,
  NAVIGATION_FROM_IS_ASSISTED_ORDERING,
  ORDERING_FOR_SELECTED_FARMER,
} from '@/constants/common'
import { ORDER_STATUS } from '@/constants/order'
import { HYP_PAYMENT_MODE } from '@/constants/payments'
import { CartContext } from '@/context/CartProvider'
import { InsuranceContext } from '@/context/InsuranceProvider'
import { UserContext } from '@/context/UserProvider'
import { InsuranceDataItem } from '@/models/api/productInsurance'
import { AvailbleStockError, CartValidationError, OTPData } from '@/models/Cart'
import { ComboVariant2 } from '@/models/Combo'
import { AvailableCoupon } from '@/models/Coupon'
import { ValidateOrder } from '@/models/Order'
import { CartStateInStorage, ProductVariant } from '@/models/Product'
import { VrNetworkList } from '@/utils/api/fetchVrNetwork'
import { createOrderQuery, updateCartIdInLocalStorage } from '@/utils/cart'
import {
  getCookieValue,
  getLocalStorageKey,
  isArray,
  isSessionStorageAvailable,
} from '@/utils/helper'

const useCartValidation = () => {
  const [validating, setValidating] = useState(false)
  const [validationError, setValidationError] =
    useState<CartValidationError | null>(null)
  const [errorCode, setErrorCode] = useState<string>()
  const [availableCoupons, setAvailableCoupons] = useState<AvailableCoupon[]>(
    [],
  )
  const [otpData, setOTPData] = useState<OTPData>()
  const [isPayNowEnabled, setIsPayNowEnabled] = useState(false)
  const [prePayAmount, setPrePayAmount] = useState(NaN)
  const [paymentOptions, setPaymentOptions] = useState<PaymentOptions>()
  const [appliedCoupon, setAppliedCoupon] = useState<AvailableCoupon | null>(
    null,
  )
  const [cumulative_commission, setCumulativeCommission] = useState<number>(0)
  const orderingFor =
    getCookieValue(NAVIGATION_FROM_IS_ASSISTED_ORDERING) === 'true'
  const storeFarmer =
    typeof window !== 'undefined' && isSessionStorageAvailable(window)
      ? sessionStorage.getItem(ORDERING_FOR_SELECTED_FARMER)
      : null
  const selectedFarmer: VrNetworkList | null = storeFarmer
    ? JSON.parse(storeFarmer)
    : null
  const assumeUserNumber = getCookieValue(ASSISTED_USER_NUMBER)

  const {
    cartId,
    cartItems,
    comboItems,
    cartType,
    isPickupOrder,
    pickupFarmerDetails,
    updateCartState,
  } = useContext(CartContext)
  const { insuranceItems } = useContext(InsuranceContext)
  const { user } = useContext(UserContext)
  const isPrebookOrder = getLocalStorageKey<CartStateInStorage, null>(
    MY_CART,
    null,
  )?.[cartType].isPrebook
  const { t } = useTranslation('cartInsurance')

  const handleValidateSuccess = useCallback((response: unknown) => {
    if (response) {
      const {
        cart_id,
        coupons,
        coupon,
        pay_now,
        payment_options,
        prepay_amount,
        cumulative_commission,
        is_otp_required,
        otp_template_variables,
      } = response as ValidateOrder
      if (cart_id && cartId !== cart_id) updateCartState('cartId', cart_id)
      updateCartIdInLocalStorage(cart_id)
      setOTPData({
        required: is_otp_required,
        template: otp_template_variables,
      })
      setIsPayNowEnabled(pay_now)
      setPaymentOptions(payment_options)
      setPrePayAmount(prepay_amount || NaN)
      setCumulativeCommission(cumulative_commission || 0)
      setValidationError(null) // Clear validation error if revalidation gives response
      setValidating(false)
      setErrorCode(undefined)
      setAvailableCoupons(
        (coupon
          ? coupons?.map((c) => ({
              ...c,
              is_applied: c.couponCode === coupon.code,
            }))
          : coupons) ?? [],
      )
    }
  }, [])

  const handlValidateError = useCallback(
    (e: unknown) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const error: any = (e as AxiosError).response?.data
      // Handle error when available quantity at vendor's end is less than selected quantity
      if (error?.non_field_errors) {
        try {
          const availableQuantities: Record<string, number> = {}
          const availableComboQuantities: Record<string, number> = {}
          const errorArr: AvailbleStockError[] = JSON.parse(
            error.non_field_errors,
          )

          errorArr.forEach((item) => {
            const key = item.item_id
            if (item?.parent_type === 'COMBO') {
              availableComboQuantities[key] = item.available_qty
            } else {
              availableQuantities[key] = item.available_qty
            }
          })

          setErrorCode('item_quantity_error')
          setValidationError({
            message: JSON.stringify(availableQuantities),
            comboMessage: JSON.stringify(availableComboQuantities),
          })
          if (isSessionStorageAvailable(window)) {
            const availableQtyInSS = sessionStorage.getItem(AVAILABLE_STOCK)
            const availableComboQtyInSS = sessionStorage.getItem(
              AVAILABLE_COMBO_STOCK,
            )
            sessionStorage.setItem(
              AVAILABLE_COMBO_STOCK,

              JSON.stringify(
                availableComboQtyInSS
                  ? {
                      ...JSON.parse(availableComboQtyInSS),
                      ...availableComboQuantities,
                    }
                  : availableComboQuantities,
              ),
            )
            sessionStorage.setItem(
              AVAILABLE_STOCK,

              JSON.stringify(
                availableQtyInSS
                  ? {
                      ...JSON.parse(availableQtyInSS),
                      ...availableQuantities,
                    }
                  : availableQuantities,
              ),
            )
          }
        } catch {
          // If error could not be parsed than it's most likely a simple error message
          setValidationError({ message: error.non_field_errors[0] })
        }
      }
      const productInactiveError: Error = error?.error
      if (
        productInactiveError &&
        productInactiveError.code &&
        isErrorCodeFromCart(productInactiveError.code)
      ) {
        setErrorCode(productInactiveError.code)
        setValidationError({
          message: productInactiveError.message,
          details: productInactiveError.details,
        })
      } else {
        const errorCode = (e as Error)?.code
        if (errorCode && isErrorCodeFromCart(errorCode)) {
          setErrorCode((e as Error).code)
          setValidationError({
            message: (e as Error).message,
            details: (e as Error).details,
          })
        } else if (error?.key && error.key === 'DC_QUOTA_EXHAUSTED') {
          setValidationError({
            message: t('dc_quota_error', {
              quota: error.value,
            }),
          })
        } else if (error?.insurance_type) {
          setValidationError({
            message: t('cart_amt_error', {
              minimum: error.cart_threshold_data.minimum_cart_value,
              maximum: error.cart_threshold_data.maximum_cart_value,
            }),
          })
        } else if (error?.detail) {
          setValidationError({ message: error.detail })
        }
      }
      setValidating(false)
    },
    [t],
  )

  const validateCart = useCallback(
    async (
      selectedAddress: Address | null,
      vendor: Vendor,
      validateWithCoupon: boolean,
      appliedCoupon: AvailableCoupon | null,
      variants: ProductVariant[],
      idKycStatus: boolean,
      bankKycStatus: boolean,
      totalVariantCost: number,
      totalInsuranceCost: number,
      totalDiscount: number,
      shippingCharges: number,
      insuranceData: InsuranceDataItem[],
      isCreditOrder: boolean,
      comboVariants: ComboVariant2[],
    ) => {
      setValidating(true)
      let hasRetried = false
      const createQuery = (withCartId = true) =>
        createOrderQuery(
          isPickupOrder,
          (isPickupOrder
            ? pickupFarmerDetails?.phone_number
            : (selectedFarmer?.phone_number || assumeUserNumber) && orderingFor
              ? selectedFarmer?.phone_number || assumeUserNumber
              : selectedAddress
                ? selectedAddress?.contact_number
                : user?.phone_number) || '',
          withCartId ? cartId : '', // remove cartId on retry
          variants,
          totalVariantCost,
          totalInsuranceCost,
          totalDiscount,
          shippingCharges,
          validateWithCoupon,
          cartItems,
          appliedCoupon,
          selectedAddress,
          insuranceItems,
          insuranceData,
          idKycStatus,
          bankKycStatus,
          '',
          vendor,
          '',
          false,
          0,
          isCreditOrder,
          isCreditOrder ? HYP_PAYMENT_MODE.CREDIT : HYP_PAYMENT_MODE.CASH,
          comboVariants || [],
          comboItems,
        )

      const query = createQuery()

      if (isPrebookOrder) {
        query.status = ORDER_STATUS.PreOrder
      }
      const locale = getCookieValue(LOCALE_COOKIE_NAME) || 'hi'

      try {
        const response = await validateOrderV2(query, {
          'X-LANGUAGE': locale,
        } as unknown as AxiosHeaders)
        handleValidateSuccess(response)
      } catch (e) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const error: any = (e as AxiosError).response?.data
        const productInactiveError: Error = error?.error
        // Retry once without cartId if CART_401 or CART_404
        if (
          (productInactiveError?.code === CART_ERRORS.CART_401 ||
            productInactiveError?.code === CART_ERRORS.CART_404) &&
          !hasRetried
        ) {
          try {
            const retryQuery = createQuery(false)
            hasRetried = true
            const retryResponse = await validateOrderV2(retryQuery)
            handleValidateSuccess(retryResponse)
            return
          } catch (retryErr) {
            handlValidateError(retryErr)
          }
        } else {
          handlValidateError(e)
        }
      }
      setValidating(false)
    },
    [
      isPrebookOrder,
      isPickupOrder,
      pickupFarmerDetails?.phone_number,
      user?.phone_number,
      cartId,
      cartItems,
      insuranceItems,
      comboItems,
      handleValidateSuccess,
      handlValidateError,
    ],
  )

  // Apply available coupon
  useEffect(() => {
    if (isArray(availableCoupons as []) && appliedCoupon === null) {
      setAppliedCoupon(
        availableCoupons.find((coupon) => coupon.is_applied) || null,
      )
    }
    if (availableCoupons?.length === 0) {
      setAppliedCoupon(null)
    }
    // Do not add appliedCoupon in the dependencies as we don't want coupon to get automatically set when user removes it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCoupons])
  useEffect(() => {
    if (isArray(availableCoupons as [])) {
      setAppliedCoupon(availableCoupons.find((c) => c.is_applied) || null)
    }
  }, [availableCoupons, cartItems])

  return {
    appliedCoupon,
    availableCoupons,
    setAppliedCoupon,
    setValidationError,
    validateCart,
    validating,
    validationError,
    isPayNowEnabled,
    paymentOptions,
    prePayAmount,
    errorCode,
    cumulative_commission,
    otpData,
  }
}

export default useCartValidation
